import React, { useState, useEffect, useRef, Fragment } from 'react'
import QuestHOC from './QuestHOC'
import JourneyCard from './JourneyCard'
import JourneyEditor from './JourneyEditor'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const truncateLabel = (text) => {
  const t = String(text || '').trim()
  if (t.length > 72) return `${t.slice(0, 69)}\u2026`
  return t
}

// Function to create a fresh journey section
const createFreshJourney = () => ({
  title: '',
  description: '',
  body: '',
  imageFileName: '',
  isUploading: false,
})

// Function to clean journey data for API (remove UI-only fields)
const cleanJourneyForAPI = (journey) => {
  const { isUploading, _tempId, ...cleanJourney } = journey
  return cleanJourney
}

const hasJourneyContent = (journey) =>
  !!(
    journey?.title?.trim() ||
    journey?.description?.trim() ||
    journey?.body?.trim() ||
    journey?.imageFileName
  )

const QuestJourney = ({
  questDetail,
  updateQuestDetails,
  isFormSubmitting,
}) => {
  // Journey sections state
  const [journeySections, setJourneySections] = useState([])
  const journeySectionsRef = useRef(journeySections)

  // Inline editor state (replaces modal)
  const [journeyEditor, setJourneyEditor] = useState({
    open: false,
    journey: null,
    journeyIndex: null,
  })
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    index: null,
    label: '',
  })

  useEffect(() => {
    journeySectionsRef.current = journeySections
  }, [journeySections])

  useEffect(() => {
    const journeyData = questDetail?.journeySections
    if (journeyData?.length > 0) {
      const sortedJourneys = [...journeyData].sort(
        (a, b) => a.sequenceNumber - b.sequenceNumber,
      )
      // Add unique IDs for tracking if they don't exist
      const journeysWithIds = sortedJourneys.map((journey, index) => ({
        ...journey,
        _tempId: journey._tempId || `journey-${Date.now()}-${index}`,
      }))
      setJourneySections(journeysWithIds)
    } else {
      setJourneySections([])
    }
  }, [questDetail])

  useEffect(() => {
    if (!journeyEditor.open) {
      return undefined
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeJourneyEditor()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [journeyEditor.open])

  const closeJourneyEditor = () => {
    setJourneyEditor({
      open: false,
      journey: null,
      journeyIndex: null,
    })
  }

  const requestJourneyDelete = (idx) => {
    const journey = journeySections[idx]
    setDeleteModal({
      show: true,
      index: idx,
      label: journey?.title?.trim() || 'Untitled section',
    })
  }

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, index: null, label: '' })
  }

  const confirmJourneyDelete = () => {
    const idx = deleteModal.index
    closeDeleteModal()
    if (typeof idx !== 'number') return
    if (journeyEditor.open && journeyEditor.journeyIndex === idx) {
      closeJourneyEditor()
    }
    const newJourneys = journeySections.filter((_, index) => index !== idx)
    const updatedJourneys = newJourneys.map((journey, index) => ({
      ...journey,
      sequenceNumber: index + 1,
    }))

    setJourneySections(updatedJourneys)
    updateQuestDetails({
      journeySections: updatedJourneys.map(cleanJourneyForAPI),
    })
  }

  // Function to handle journey card click (open inline editor under row)
  const handleJourneyEdit = (journey, index) => {
    if (journeyEditor.open && journeyEditor.journeyIndex === index) {
      closeJourneyEditor()
      return
    }
    setJourneyEditor({
      open: true,
      journey: journey,
      journeyIndex: index,
    })
  }

  // Function to handle adding a new journey - opens blank add-form at bottom
  const handleAddJourney = () => {
    const freshJourney = createFreshJourney()

    setJourneyEditor({
      open: true,
      journey: freshJourney,
      journeyIndex: journeySections.length, // New journey index
    })
  }

  const adjustEditingIndex = (dragIndex, hoverIndex, currentIndex) => {
    if (typeof currentIndex !== 'number') {
      return currentIndex
    }
    if (currentIndex === dragIndex) {
      return hoverIndex
    }
    if (dragIndex < currentIndex && hoverIndex >= currentIndex) {
      return currentIndex - 1
    }
    if (dragIndex > currentIndex && hoverIndex <= currentIndex) {
      return currentIndex + 1
    }
    return currentIndex
  }

  const handleJourneyMove = (dragIndex, hoverIndex) => {
    const nextJourneys = [...journeySectionsRef.current]
    const [moved] = nextJourneys.splice(dragIndex, 1)
    nextJourneys.splice(hoverIndex, 0, moved)
    const updatedJourneys = nextJourneys.map((journey, index) => ({
      ...journey,
      sequenceNumber: index + 1,
    }))

    journeySectionsRef.current = updatedJourneys
    setJourneySections(updatedJourneys)

    if (journeyEditor.open) {
      const nextIndex = adjustEditingIndex(
        dragIndex,
        hoverIndex,
        journeyEditor.journeyIndex,
      )
      if (nextIndex !== journeyEditor.journeyIndex) {
        setJourneyEditor((prev) => ({
          ...prev,
          journeyIndex: nextIndex,
          journey:
            typeof nextIndex === 'number'
              ? updatedJourneys[nextIndex]
              : prev.journey,
        }))
      }
    }
  }

  const persistAfterReorder = () => {
    updateQuestDetails({
      journeySections: journeySectionsRef.current.map(cleanJourneyForAPI),
    })
  }

  const handleJourneySave = (journeyData) => {
    const { journeyIndex } = journeyEditor
    const isNew = journeyIndex >= journeySections.length

    if (isNew && !hasJourneyContent(journeyData)) {
      closeJourneyEditor()
      return
    }

    if (journeyIndex < journeySections.length) {
      const updatedJourneys = [...journeySections]
      updatedJourneys[journeyIndex] = {
        ...journeyData,
        sequenceNumber: journeyIndex + 1,
      }
      setJourneySections(updatedJourneys)
      updateQuestDetails({
        journeySections: updatedJourneys.map(cleanJourneyForAPI),
      })
    } else {
      const newJourneys = [
        ...journeySections,
        {
          ...journeyData,
          sequenceNumber: journeySections.length + 1,
          _tempId: `journey-${Date.now()}-${journeySections.length}`,
        },
      ]
      setJourneySections(newJourneys)
      updateQuestDetails({
        journeySections: newJourneys.map(cleanJourneyForAPI),
      })
    }
    closeJourneyEditor()
  }

  const isAdding =
    journeyEditor.open && journeyEditor.journeyIndex >= journeySections.length

  const deleteName = truncateLabel(deleteModal.label)
  const deleteMessage = deleteName
    ? `\u201C${deleteName}\u201D will be removed. This phase will be removed from the Prosperity page.`
    : 'This phase will be removed from the Prosperity page.'

  return (
    <div className='quest-journey-sections'>
      <p className='experience-f-hint-top text-dark'>
        Marketing phases for the Prosperity page. This is not the dated
        itinerary participants see after they enroll.
      </p>
      {journeySections.length === 0 && !journeyEditor.open ? (
        <div className='empty-state'>
          <div className='empty-title text-center'>
            <h5>No journey sections created yet</h5>
          </div>
          <div className='text-center mt-3'>
            <button
              type='button'
              className='experience-btn-sm btn-outline-experience'
              onClick={handleAddJourney}>
              Add New Section
            </button>
          </div>
        </div>
      ) : (
        <div className='experience-lrow-list' data-add-list='journey'>
          {journeySections.map((journey, index) => {
            const isEditing =
              journeyEditor.open && journeyEditor.journeyIndex === index
            return (
              <Fragment key={journey._tempId || index}>
                <JourneyCard
                  journey={journey}
                  index={index}
                  isEditing={isEditing}
                  onEdit={handleJourneyEdit}
                  onDelete={() => requestJourneyDelete(index)}
                  moveItem={handleJourneyMove}
                  onDragEnd={persistAfterReorder}
                  isUploading={journey.isUploading}
                />
                {isEditing ? (
                  <div className='experience-add-form'>
                    <JourneyEditor
                      key={journey._tempId || `edit-${index}`}
                      journey={journeyEditor.journey}
                      journeyIndex={journeyEditor.journeyIndex}
                      onSave={handleJourneySave}
                      onCancel={closeJourneyEditor}
                      isSaving={isFormSubmitting}
                    />
                  </div>
                ) : null}
              </Fragment>
            )
          })}

          {isAdding ? (
            <div className='experience-add-form'>
              <JourneyEditor
                key='new-journey'
                journey={journeyEditor.journey}
                journeyIndex={journeyEditor.journeyIndex}
                onSave={handleJourneySave}
                onCancel={closeJourneyEditor}
                isSaving={isFormSubmitting}
              />
            </div>
          ) : null}

          {!journeyEditor.open ? (
            <div className='experience-form-actions'>
              <button
                type='button'
                className='experience-btn-sm btn-outline-experience'
                onClick={handleAddJourney}>
                Add New Section
              </button>
            </div>
          ) : null}
        </div>
      )}

      <ExperienceConfirmModal
        show={deleteModal.show}
        onHide={closeDeleteModal}
        onConfirm={confirmJourneyDelete}
        title='Delete this journey section?'
        message={deleteMessage}
        confirmLabel='Delete'
        isLoading={isFormSubmitting}
      />
    </div>
  )
}

export default QuestHOC(QuestJourney)
